import { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'

const getUseStyles = (theme: Theme) =>
  makeStyles(() => ({
    launcher: {
      // MUI v5's IconButton sets `position: relative` and its emotion styles are
      // injected after this JSS sheet, so it wins without !important - which left
      // the launcher in normal document flow, far below the viewport.
      position: 'fixed !important' as 'fixed',
      right: '24px',
      bottom: '24px',
      zIndex: 1000000,
      width: '52px',
      height: '52px',
      backgroundColor: `${theme.palette.primary.main} !important`,
      color: '#fff !important',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.22)',
      '&:hover': {
        filter: 'brightness(1.08)',
      },
    },
    panel: {
      position: 'fixed',
      right: '24px',
      bottom: '24px',
      // topBannerBox notifications are fixed at z-index 999999 and overlap this
      // panel's horizontal band, so anything lower gets covered by them.
      // Stays below the Dash loading mask (9999999).
      zIndex: 1000000,
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 'calc(100vw - 32px)',
      height: 'min(620px, calc(100vh - 120px))',
      borderRadius: '12px',
      overflow: 'hidden',
      backgroundColor:
        theme.palette.custom?.dialogBg ?? theme.palette.background.paper,
      border: `1px solid ${theme.palette.custom?.paperBorder ?? 'transparent'}`,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.24)',
    },
    consent: {
      flex: 1,
      overflowY: 'auto',
      padding: '18px 18px 22px',
    },
    consentTitle: {
      margin: '0 0 10px',
      fontSize: '14px',
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    consentText: {
      margin: '0 0 10px',
      fontSize: '13px',
      lineHeight: '19px',
      color: theme.palette.text.secondary,
    },
    consentButton: {
      marginTop: '6px',
      padding: '9px 18px',
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
      borderRadius: '7px',
      border: 'none',
      color: '#fff',
      backgroundColor: theme.palette.primary.main,
    },
    ticketEmpty: {
      padding: '6px 8px 4px',
      fontSize: '12px',
      lineHeight: '17px',
      color: theme.palette.text.secondary,
    },
    ticketItem: {
      padding: '7px 8px',
      borderRadius: '6px',
      '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    ticketTop: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '6px',
    },
    ticketRef: {
      fontSize: '11.5px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      color: theme.palette.text.secondary,
    },
    ticketStatus: {
      fontSize: '10.5px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      padding: '1px 7px',
      borderRadius: '999px',
    },
    statusOpen: { color: '#e0603a', backgroundColor: 'rgba(224,96,58,0.14)' },
    statusOngoing: {
      color: '#d99b25',
      backgroundColor: 'rgba(217,155,37,0.14)',
    },
    statusDone: { color: '#2fa36b', backgroundColor: 'rgba(47,163,107,0.14)' },
    ticketSubject: {
      marginTop: '3px',
      fontSize: '12.5px',
      lineHeight: '17px',
      color: theme.palette.text.primary,
    },
    ticketMeta: {
      marginTop: '2px',
      fontSize: '11px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      color: theme.palette.text.secondary,
    },
    ticketReply: {
      marginTop: '6px',
      padding: '7px 9px',
      borderRadius: '6px',
      fontSize: '12px',
      lineHeight: '17px',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
      borderLeft: `2px solid ${theme.palette.primary.main}`,
    },
    ticketReplyWho: {
      display: 'block',
      marginBottom: '3px',
      fontSize: '10.5px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: theme.palette.primary.main,
    },
    ticketBtn: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    ticketBadge: {
      position: 'absolute',
      top: '-5px',
      right: '-7px',
      minWidth: '15px',
      height: '15px',
      padding: '0 4px',
      borderRadius: '999px',
      fontSize: '10px',
      fontWeight: 700,
      lineHeight: '15px',
      textAlign: 'center',
      color: '#fff',
      backgroundColor: theme.palette.primary.main,
    },
    // Tickets get the full panel rather than a cramped strip under the chat
    // list, so a reply from the team is actually readable.
    ticketsView: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
    ticketsHead: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px 8px',
    },
    ticketsTitle: {
      fontSize: '13px',
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    ticketsBack: {
      padding: 0,
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '12.5px',
      color: theme.palette.primary.main,
    },
    ticketsBody: {
      flex: 1,
      overflowY: 'auto',
      padding: '0 16px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    ticketCard: {
      padding: '11px 12px',
      borderRadius: '9px',
      backgroundColor:
        theme.palette.custom?.tipBg ?? theme.palette.action.hover,
      border: `1px solid ${theme.palette.custom?.tableBorder ?? 'transparent'}`,
    },
    handoverBanner: {
      padding: '9px 12px',
      marginBottom: '4px',
      borderRadius: '8px',
      fontSize: '12.5px',
      lineHeight: '17px',
      color: '#2fa36b',
      backgroundColor: 'rgba(47,163,107,0.10)',
      border: '1px solid rgba(47,163,107,0.3)',
    },
    supportRow: {
      display: 'flex',
      gap: '9px',
      alignItems: 'flex-start',
      margin: '2px 0',
    },
    supportAvatar: {
      flexShrink: 0,
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '10.5px',
      fontWeight: 700,
      letterSpacing: '0.02em',
      color: '#fff',
      backgroundColor: '#2fa36b',
    },
    supportBody: {
      flex: 1,
      minWidth: 0,
    },
    supportHeader: {
      display: 'flex',
      alignItems: 'baseline',
      gap: '7px',
      marginBottom: '3px',
    },
    supportName: {
      fontSize: '12px',
      fontWeight: 650,
      color: '#2fa36b',
    },
    supportTime: {
      fontSize: '10.5px',
      color: theme.palette.text.secondary,
    },
    supportBubble: {
      padding: '9px 12px',
      borderRadius: '2px 12px 12px 12px',
      fontSize: '14px',
      lineHeight: '20px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      color: theme.palette.text.primary,
      backgroundColor: 'rgba(47,163,107,0.10)',
      border: '1px solid rgba(47,163,107,0.28)',
    },
    thumbRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginTop: '8px',
    },
    thumb: {
      width: '84px',
      height: '84px',
      objectFit: 'cover',
      borderRadius: '7px',
      display: 'block',
      border: `1px solid ${
        theme.palette.custom?.tableBorder ?? 'rgba(128,128,128,.3)'
      }`,
      cursor: 'zoom-in',
    },
    pendingBar: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px',
      padding: '8px 16px 0',
    },
    pendingThumb: {
      position: 'relative',
      '& img': {
        width: '52px',
        height: '52px',
        objectFit: 'cover',
        borderRadius: '6px',
        display: 'block',
        border: `1px solid ${
          theme.palette.custom?.inputBorder ?? 'rgba(128,128,128,.3)'
        }`,
      },
      '& button': {
        position: 'absolute',
        top: '-6px',
        right: '-6px',
        width: '18px',
        height: '18px',
        lineHeight: '16px',
        padding: 0,
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#fff',
        backgroundColor: 'rgba(0,0,0,0.65)',
      },
    },
    imgError: {
      fontSize: '11.5px',
      color: '#e0603a',
    },
    resizeHandle: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '6px',
      cursor: 'ew-resize',
      zIndex: 2,
      '&:hover': { backgroundColor: theme.palette.primary.main, opacity: 0.35 },
    },
    headerAction: {
      fontSize: '19px !important',
      color: theme.palette.text.secondary,
    },
    historyList: {
      maxHeight: '220px',
      overflowY: 'auto',
      padding: '8px',
      borderBottom: `1px solid ${
        theme.palette.custom?.tableBorder ?? 'transparent'
      }`,
    },
    historyHint: {
      padding: '2px 6px 6px',
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: theme.palette.text.secondary,
    },
    historyItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '4px',
      borderRadius: '6px',
      padding: '2px 2px 2px 8px',
      '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    historyItemActive: {
      backgroundColor: theme.palette.action.selected,
    },
    historyItemLabel: {
      flex: 1,
      minWidth: 0,
      fontSize: '13px',
      cursor: 'pointer',
      padding: '6px 0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: theme.palette.text.primary,
    },
    historyDelete: {
      fontSize: '17px !important',
      color: theme.palette.text.secondary,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 12px 12px 16px',
      borderBottom: `1px solid ${
        theme.palette.custom?.tableBorder ?? 'transparent'
      }`,
    },
    headerIcon: {
      fontSize: '20px !important',
      color: theme.palette.primary.main,
    },
    headerTitle: {
      fontSize: '15px',
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    closeIcon: {
      fontSize: '20px !important',
      color: theme.palette.text.secondary,
    },
    body: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    empty: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    emptyTitle: {
      margin: '0 0 4px',
      fontSize: '14px',
      color: theme.palette.text.secondary,
    },
    suggestion: {
      textAlign: 'left',
      padding: '10px 12px',
      fontSize: '13px',
      cursor: 'pointer',
      borderRadius: '8px',
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${theme.palette.custom?.inputBorder ?? 'transparent'}`,
      '&:hover': {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
      },
    },
    userRow: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    assistantRow: {
      display: 'flex',
      justifyContent: 'flex-start',
    },
    userBubble: {
      maxWidth: '85%',
      padding: '9px 13px',
      borderRadius: '12px 12px 2px 12px',
      fontSize: '14px',
      lineHeight: '20px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      color: '#fff',
      backgroundColor: theme.palette.primary.main,
    },
    assistantBubble: {
      maxWidth: '92%',
      padding: '9px 13px',
      borderRadius: '12px 12px 12px 2px',
      fontSize: '14px',
      lineHeight: '20px',
      wordBreak: 'break-word',
      color: theme.palette.text.primary,
      backgroundColor:
        theme.palette.custom?.tipBg ?? theme.palette.action.hover,
    },
    streamingText: {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontSize: '14px',
      lineHeight: '20px',
    },
    markdown: {
      // Long app keys and URLs must not push the bubble wider than the panel.
      overflowWrap: 'anywhere',
      '& p': { margin: '0 0 8px' },
      '& p:last-child': { marginBottom: 0 },
      '& ul, & ol': { margin: '0 0 8px', paddingLeft: '20px' },
      '& li': { marginBottom: '2px' },
      '& li > p': { margin: 0 },
      '& h1, & h2, & h3, & h4': {
        margin: '12px 0 6px',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      '& h1': { fontSize: '16px' },
      '& h2': { fontSize: '15px' },
      '& h3, & h4': { fontSize: '14px' },
      '& *:first-child': { marginTop: 0 },
      // display:block makes the table itself the scroll container, so a wide
      // table scrolls sideways instead of stretching the chat panel.
      '& table': {
        display: 'block',
        width: 'max-content',
        maxWidth: '100%',
        overflowX: 'auto',
        borderCollapse: 'collapse',
        margin: '8px 0',
        fontSize: '12.5px',
      },
      '& th, & td': {
        padding: '5px 8px',
        textAlign: 'left',
        whiteSpace: 'nowrap',
        color: theme.palette.text.primary,
        border: `1px solid ${
          theme.palette.custom?.tableBorder ?? 'rgba(128,128,128,0.35)'
        }`,
      },
      '& th': {
        fontWeight: 600,
        // custom.tableHead is a TEXT colour in this theme - white in dark mode,
        // dark grey in light. Using it as a background produced white-on-white
        // headers in dark mode and dark-on-dark in light mode.
        color: theme.palette.custom?.tableHead ?? theme.palette.text.primary,
        backgroundColor: theme.palette.action.hover,
      },
      '& code': {
        padding: '1px 4px',
        borderRadius: '3px',
        fontSize: '12px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        backgroundColor: theme.palette.action.hover,
      },
      '& pre': {
        margin: '8px 0',
        padding: '8px 10px',
        borderRadius: '6px',
        overflowX: 'auto',
        backgroundColor: theme.palette.action.hover,
      },
      '& pre code': { padding: 0, backgroundColor: 'transparent' },
      '& blockquote': {
        margin: '8px 0',
        paddingLeft: '10px',
        borderLeft: `3px solid ${
          theme.palette.custom?.tableBorder ?? 'rgba(128,128,128,0.35)'
        }`,
        color: theme.palette.text.secondary,
      },
      '& hr': {
        border: 'none',
        borderTop: `1px solid ${
          theme.palette.custom?.tableBorder ?? 'rgba(128,128,128,0.35)'
        }`,
        margin: '12px 0',
      },
      '& a': { color: theme.palette.primary.main },
      '& strong': { fontWeight: 600 },
    },
    activity: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '13px',
      fontStyle: 'italic',
      color: theme.palette.text.secondary,
    },
    activityDot: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      animation: '$pulse 1.2s ease-in-out infinite',
    },
    '@keyframes pulse': {
      '0%, 100%': { opacity: 0.3 },
      '50%': { opacity: 1 },
    },
    footer: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '8px',
      padding: '12px 16px',
      borderTop: `1px solid ${
        theme.palette.custom?.tableBorder ?? 'transparent'
      }`,
    },
    sendIcon: {
      fontSize: '20px !important',
      color: theme.palette.primary.main,
    },
    stopButton: {
      padding: '6px 12px',
      fontSize: '13px',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      borderRadius: '6px',
      color: theme.palette.text.secondary,
      backgroundColor: 'transparent',
      border: `1px solid ${theme.palette.custom?.inputBorder ?? 'transparent'}`,
    },
  }))

export default getUseStyles
